/**
 * Dashboard Layout - Navigation and layout wrapper for authenticated pages
 * Clean, professional design with sidebar navigation
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  BarChart3,
  FileText,
  Settings,
  Plus,
  Menu,
  X,
  LogOut,
  User,
  Bell,
  Search,
  HelpCircle,
  Filter,
  Workflow,
  Users,
  Briefcase,
  BookOpen,
  GitMerge,
  Layers,
  Layers3,
  PieChart,
  FolderLock,
  Gauge,
  Flame,
  Heart,
  Landmark,
  KeyRound,
  Link2,
  Inbox,
  MessageSquare,
  Activity,
  Target,
  Upload as UploadIcon,
  Sparkles,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import leadAdminService from '../services/leadAdminService';
import api from '../services/api';
import SystemHealthBanner from './SystemHealthBanner';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

// Shape of an autocomplete row — kept minimal so we can render
// company + investor results in the same dropdown without a discriminated
// union getting unwieldy.
interface SearchHit {
  kind: 'company' | 'investor';
  id: number;
  name: string;
  secondary?: string | null; // industry for company, firm for investor
}


const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newLeadCount, setNewLeadCount] = useState<number | null>(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHits, setSearchHits] = useState<SearchHit[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  // Side-menu group expand/collapse — only Vetting expanded by default,
  // others collapsed so the sidebar stays tidy. Operator's preference
  // is persisted in localStorage so it sticks across sessions.
  const COLLAPSE_KEY = 'vcre_sidebar_groups_v1';
  const DEFAULT_EXPANDED = new Set(['top', 'vetting']);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* fall through to default */ }
    return DEFAULT_EXPANDED;
  });
  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };
  // Auto-expand whatever group contains the current route so the active
  // item is always visible — without losing the user's other choices.
  const expandIfActive = (groupId: string, hasActive: boolean) => {
    if (hasActive && !expandedGroups.has(groupId)) {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.add(groupId);
        return next;
      });
    }
  };
  const location = useLocation();
  const navigate = useNavigate();

  // Debounced typeahead — hits both company-search and investor-list
  // (which supports a ?search= param) in parallel and shows the union
  // grouped by kind. Replaces the old form-submit flow that always
  // routed to /companies regardless of query intent.
  useEffect(() => {
    const q = globalSearch.trim();
    if (q.length < 2) {
      setSearchHits([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const [coRes, invRes] = await Promise.all([
          api.get('/companies/search', { params: { q, limit: 6 } }).catch(() => null),
          api.get('/investors', { params: { search: q, size: 6, min_completion: 0 } }).catch(() => null),
        ]);
        const hits: SearchHit[] = [];
        const cos = coRes?.data?.results || [];
        for (const c of cos.slice(0, 6)) {
          hits.push({
            kind: 'company',
            id: c.id,
            name: c.name,
            secondary: c.industry || null,
          });
        }
        const invs = invRes?.data?.investors || [];
        for (const i of invs.slice(0, 6)) {
          hits.push({
            kind: 'investor',
            id: i.id,
            name: i.full_name || i.firm_name || '(unnamed)',
            secondary: i.firm_name || null,
          });
        }
        setSearchHits(hits);
        setHighlightIdx(0);
      } finally {
        setSearchLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [globalSearch]);

  const goToHit = (hit: SearchHit) => {
    setGlobalSearch('');
    setSearchHits([]);
    setSearchOpen(false);
    if (hit.kind === 'company') navigate(`/companies/${hit.id}`);
    else navigate(`/investors/${hit.id}`);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, Math.max(searchHits.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const q = globalSearch.trim();
      if (searchHits.length > 0) {
        goToHit(searchHits[highlightIdx] || searchHits[0]);
      } else if (q) {
        // Fallback: send to a results-style page on Companies (preserves
        // the old behaviour as a last resort if neither search returned
        // anything).
        navigate(`/companies?search=${encodeURIComponent(q)}`);
        setGlobalSearch('');
        setSearchOpen(false);
      }
    } else if (e.key === 'Escape') {
      setSearchOpen(false);
    }
  };
  const { user, signOut } = useAuthStore();

  // Fetch the count of leads with status='new' so the sidebar can show an
  // unread pill. Failures are silent — the badge just won't render.
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      const result = await leadAdminService.fetchLeads({ status: 'new', size: 1 });
      if (!cancelled && result.data) {
        setNewLeadCount(result.data.total);
      }
    };
    fetchCount();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Side menu organized around the platform's mental model:
  //   Vetting → Matching → Deals → Predictions
  // Plus parallel CRM track and a collapsed Settings tray.
  const navigationGroups = [
    {
      id: 'top',
      header: null,
      items: [
        { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: BarChart3, current: location.pathname === '/dashboard' },
      ],
    },
    {
      id: 'vetting',
      header: 'Vetting',
      items: [
        { id: 'companies',         name: 'Companies',         href: '/companies',         icon: TrendingUp, current: location.pathname.includes('/companies') && !location.pathname.startsWith('/companies/upload') },
        { id: 'investors',         name: 'Investors',         href: '/investors',         icon: Users,      current: location.pathname.includes('/investors') && !/investor_type=(philanthropy|corporate_philanthropy|lp_)/.test(location.search) },
        { id: 'philanthropies',    name: 'Philanthropies',    href: '/investors?investor_type=philanthropy', icon: Heart, current: /investor_type=(philanthropy|corporate_philanthropy)/.test(location.search) },
        { id: 'vc_funders',        name: 'VC Funders',        href: '/investors?investor_type=lp_institutional', icon: Landmark, current: /investor_type=lp_/.test(location.search) },
        { id: 'projects',          name: 'Projects',          href: '/projects',          icon: Briefcase,  current: location.pathname.includes('/projects') },
        { id: 'capital-readiness', name: 'Capital Readiness', href: '/capital-readiness', icon: Gauge,      current: location.pathname.startsWith('/capital-readiness') },
        { id: 'imports',           name: 'Imports',           href: '/imports',           icon: UploadIcon, current: location.pathname.startsWith('/imports') },
      ],
    },
    {
      id: 'matching',
      header: 'Matching',
      items: [
        { id: 'matches', name: 'Matches', href: '/matches', icon: GitMerge, current: location.pathname.includes('/matches') },
      ],
    },
    {
      id: 'deals',
      header: 'Deals',
      items: [
        // One entry — the company-centric unified view that surfaces
        // pipeline, briefs, and rooms together. Syndication + Analytics
        // stay as siblings since they're cross-cutting.
        { id: 'deals',              name: 'Deals',              href: '/deals',                   icon: Layers,     current: location.pathname === '/deals' || location.pathname.startsWith('/deals/') || location.pathname.startsWith('/deal-pipeline') || location.pathname.startsWith('/deal-rooms') },
        { id: 'syndication',        name: 'Syndication',        href: '/syndication',             icon: Layers3,    current: location.pathname.startsWith('/syndication') },
        { id: 'pipeline-analytics', name: 'Pipeline Analytics', href: '/deal-pipeline/analytics', icon: PieChart,   current: location.pathname === '/deal-pipeline/analytics' },
      ],
    },
    {
      id: 'predictions',
      header: 'Predictions',
      items: [
        { id: 'prediction-watchlist', name: 'Watchlist', href: '/prediction-watchlist', icon: Sparkles, current: location.pathname.startsWith('/prediction-watchlist') },
        { id: 'momentum',             name: 'Momentum',  href: '/momentum',             icon: Flame,    current: location.pathname.startsWith('/momentum') },
      ],
    },
    {
      id: 'crm',
      header: 'CRM',
      items: [
        { id: 'leads',        name: 'Leads',        href: '/leads',        icon: Inbox,    current: location.pathname.startsWith('/leads'), badge: newLeadCount && newLeadCount > 0 ? newLeadCount : undefined },
        { id: 'intake-chats', name: 'Intake Chats', href: '/intake-chats', icon: MessageSquare, current: location.pathname.startsWith('/intake-chats') },
        { id: 'sales-funnel', name: 'Sales Funnel', href: '/sales-funnel', icon: Filter,   current: location.pathname.includes('/sales-funnel') },
        { id: 'engagements',  name: 'Engagements',  href: '/engagements',  icon: Workflow, current: location.pathname.includes('/engagements') },
      ],
    },
    {
      id: 'settings',
      header: 'Settings',
      items: [
        { id: 'instruments',  name: 'Instruments',  href: '/instruments',           icon: BookOpen, current: location.pathname.includes('/instruments') },
        { id: 'integrations', name: 'Integrations', href: '/integrations',          icon: KeyRound, current: location.pathname.startsWith('/integrations') },
        { id: 'ai-settings',  name: 'AI Provider',  href: '/ai-settings',           icon: Sparkles, current: location.pathname.startsWith('/ai-settings') },
        { id: 'team',         name: 'Team',         href: '/team',                  icon: Users,    current: location.pathname.startsWith('/team') },
        { id: 'claude-usage', name: 'Claude Usage', href: '/settings/claude-usage', icon: Gauge,    current: location.pathname === '/settings/claude-usage' },
        { id: 'research-status', name: 'Research Status', href: '/admin/research-status', icon: Activity, current: location.pathname.startsWith('/admin/research-status') },
        { id: 'marketing', name: 'Pilot Targets', href: '/admin/marketing', icon: Target, current: location.pathname.startsWith('/admin/marketing') },
        { id: 'partner-link', name: 'Partner Link', href: '/admin/partner-link',    icon: Link2,    current: location.pathname.startsWith('/admin/partner-link') },
        { id: 'settings',     name: 'Settings',     href: '/settings',              icon: Settings, current: location.pathname === '/settings' },
      ],
    },
  ];

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* AI-outage banner — sits above everything so an admin can't miss
          that Claude/Anthropic is down (e.g. out of credits). Renders
          nothing when AI is healthy. */}
      <SystemHealthBanner />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            {/* Mobile navigation */}
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold text-gray-900">VentureOS</span>
                </div>
              </div>
              <nav className="mt-5 px-2 space-y-2">
                {navigationGroups.map((group: any) => {
                  const hasActive = group.items.some((it: any) => it.current);
                  // Always render the items if there's no header (the
                  // top "Dashboard" group). Otherwise honour the
                  // expanded state, with auto-expand when the current
                  // route lives in this group.
                  const isOpen = !group.header || expandedGroups.has(group.id) || hasActive;
                  if (group.header && hasActive) {
                    expandIfActive(group.id, true);
                  }
                  return (
                  <div key={group.id}>
                    {group.header && (
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        className="w-full flex items-center justify-between px-2 mb-1 text-[11px] uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-700"
                      >
                        <span>{group.header}</span>
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {isOpen && (
                    <div className="space-y-0.5">
                      {group.items.map((item: any) => {
                        const IconComponent = item.icon;
                        return (
                          <Link
                            key={item.id}
                            to={item.href}
                            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                              item.current
                                ? 'bg-blue-100 text-blue-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <IconComponent className="mr-4 h-6 w-6" />
                            <span className="flex-1">{item.name}</span>
                            {item.badge !== undefined && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
                })}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0 px-4 mb-8">
              <Link to="/dashboard" className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-900">VentureOS</span>
              </Link>
            </div>

            {/* Add Company CTA — analyses are now generated from the
                company detail page itself (Reports tab → Run new analysis),
                not as a separate workflow. */}
            <div className="px-4 mb-6">
              <Link
                to="/companies"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Browse Companies
              </Link>
            </div>

            {/* Navigation — grouped by Vetting · Matching · Deals · Predictions · CRM · Settings */}
            <nav className="flex-1 px-2 space-y-2">
              {navigationGroups.map((group: any) => {
                const hasActive = group.items.some((it: any) => it.current);
                const isOpen = !group.header || expandedGroups.has(group.id) || hasActive;
                if (group.header && hasActive) {
                  expandIfActive(group.id, true);
                }
                return (
                <div key={group.id}>
                  {group.header && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between px-2 mb-1 text-[11px] uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-700"
                    >
                      <span>{group.header}</span>
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  {isOpen && (
                  <div className="space-y-0.5">
                    {group.items.map((item: any) => {
                      const IconComponent = item.icon;
                      return (
                        <Link
                          key={item.id}
                          to={item.href}
                          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                            item.current
                              ? 'bg-blue-100 text-blue-900'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <IconComponent className="mr-3 h-5 w-5" />
                          <span className="flex-1">{item.name}</span>
                          {item.badge !== undefined && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                  )}
                </div>
                );
              })}
            </nav>
          </div>

          {/* User menu */}
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.company}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="ml-3 flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow-sm border-b border-gray-200">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1 px-4 flex justify-between items-center">
            {/* Search bar — global typeahead. Hits company-search and
                investor-list with the same query and renders a grouped
                results dropdown. Click or Enter on a hit navigates to
                its detail page. */}
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0 relative max-w-md">
                <div className="relative flex items-center w-full">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by name, firm, or email — companies + investors…"
                    type="search"
                    value={globalSearch}
                    onFocus={() => setSearchOpen(true)}
                    onBlur={() => window.setTimeout(() => setSearchOpen(false), 150)}
                    onChange={(e) => {
                      setGlobalSearch(e.target.value);
                      setSearchOpen(true);
                    }}
                    onKeyDown={onSearchKeyDown}
                  />
                </div>
                {searchOpen && globalSearch.trim().length >= 2 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-20">
                    {searchLoading ? (
                      <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
                    ) : searchHits.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-500">
                        No companies or investors matched &ldquo;{globalSearch.trim()}&rdquo;.
                      </div>
                    ) : (
                      <ul>
                        {(['company', 'investor'] as const).map((kind) => {
                          const group = searchHits.filter((h) => h.kind === kind);
                          if (group.length === 0) return null;
                          return (
                            <React.Fragment key={kind}>
                              <li className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-400 bg-gray-50 border-b border-gray-100">
                                {kind === 'company' ? `Companies (${group.length})` : `Investors (${group.length})`}
                              </li>
                              {group.map((h) => {
                                const idxAll = searchHits.indexOf(h);
                                const isHi = idxAll === highlightIdx;
                                return (
                                  <li key={`${h.kind}-${h.id}`}>
                                    <button
                                      type="button"
                                      onMouseDown={(ev) => { ev.preventDefault(); goToHit(h); }}
                                      onMouseEnter={() => setHighlightIdx(idxAll)}
                                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                                        isHi ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-900'
                                      }`}
                                    >
                                      {h.kind === 'company'
                                        ? <Briefcase className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                        : <Users className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                                      <span className="flex-1 min-w-0">
                                        <span className="block truncate">{h.name}</span>
                                        {h.secondary && (
                                          <span className="block truncate text-xs text-gray-500">{h.secondary}</span>
                                        )}
                                      </span>
                                    </button>
                                  </li>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right side icons */}
            <div className="ml-4 flex items-center md:ml-6 space-x-3">
              <button className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <Bell className="h-5 w-5" />
              </button>
              <button className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <HelpCircle className="h-5 w-5" />
              </button>

              {/* Mobile user menu */}
              <div className="lg:hidden">
                <button
                  onClick={handleSignOut}
                  className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;